import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  Check,
  ExternalLink,
  Gavel,
  RefreshCw,
  ShoppingBag,
  X,
  ZoomIn,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { imageUrl } from "@/features/imageUpload";
import {
  fetchStorefrontSoldCard,
  storefrontPrice,
  type SoldCard,
} from "@/features/storefront";
import {
  asCondition,
  asGame,
  asRarity,
  conditionConfig,
  gameAccent,
  pokeTypeColor,
  rarityConfig,
  type ConditionCode,
  type Rarity,
} from "@/components/shop/cardVisuals";
import { CardLightbox } from "@/components/shop/CardLightbox";
import { ManaCost } from "@/components/shop/ManaCost";
import { useStorefrontCardLookup } from "@/features/useStorefrontCardLookup";
import { useShopCart } from "./shop";

export const Route = createFileRoute("/shop/card/$id")({
  component: ShopCardDetail,
});

// ─── Sub-components ─────────────────────────────────────────────────────────

function CardImage({
  frontRef,
  backRef,
  name,
  rarity,
  onEnlarge,
}: {
  frontRef: string | null;
  backRef: string | null;
  name: string;
  rarity: Rarity;
  onEnlarge: () => void;
}) {
  const [flipped, setFlipped] = useState(false);
  const [frontErr, setFrontErr] = useState(false);
  const [backErr, setBackErr] = useState(false);

  // TNK-125: medium variant — the detail-page image renders at ~560×784.
  // Lightbox switches to original on enlarge.
  const frontUrl = frontRef ? imageUrl(frontRef, "medium") : null;
  const backUrl = backRef ? imageUrl(backRef, "medium") : null;
  const showBack = flipped && backUrl && !backErr;
  const currentUrl = showBack ? backUrl : frontUrl;
  const currentErr = showBack ? backErr : frontErr;
  const rarityCfg = rarityConfig[rarity];

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <button
        type="button"
        onClick={onEnlarge}
        aria-label="Enlarge photo"
        className="group/enlarge relative mx-auto w-full max-w-[280px] overflow-hidden rounded-xl border"
        style={{
          aspectRatio: "2.5/3.5",
          background: "linear-gradient(160deg, #0d1627 0%, #0a0f1e 100%)",
          borderColor: `${rarityCfg.color}22`,
          boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 40px ${rarityCfg.glow}`,
        }}
      >
        {currentUrl && !currentErr ? (
          <img
            src={currentUrl}
            alt={name}
            className="h-full w-full object-contain"
            onError={() => (showBack ? setBackErr(true) : setFrontErr(true))}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 opacity-30">
            <svg viewBox="0 0 80 110" className="h-auto w-16">
              <rect
                x="4"
                y="4"
                width="72"
                height="102"
                rx="6"
                fill="none"
                stroke="#7a8aaa"
                strokeWidth="1.5"
              />
              <rect
                x="10"
                y="10"
                width="60"
                height="90"
                rx="4"
                fill="none"
                stroke="#7a8aaa"
                strokeWidth="0.6"
              />
              <circle
                cx="40"
                cy="55"
                r="18"
                fill="none"
                stroke="#7a8aaa"
                strokeWidth="0.8"
              />
              <line
                x1="40"
                y1="37"
                x2="40"
                y2="73"
                stroke="#7a8aaa"
                strokeWidth="0.6"
              />
              <line
                x1="22"
                y1="55"
                x2="58"
                y2="55"
                stroke="#7a8aaa"
                strokeWidth="0.6"
              />
            </svg>
            <p className="text-center text-[10px] text-muted-foreground">
              Image not available
            </p>
          </div>
        )}
        {/* Holo shimmer for Mythic / Promo / finished cards. Animation
            class is shared; gradient is inline because it carries the
            card's rarity color. */}
        {(rarity === "Mythic" || rarity === "Promo") && (
          <div
            className="shop-holo-rarity absolute inset-0"
            style={{
              background: `linear-gradient(135deg, transparent 0%, ${rarityCfg.color}29 25%, rgba(77,158,247,0.1) 50%, ${rarityCfg.color}29 75%, transparent 100%)`,
            }}
          />
        )}

        {/* Trust signal — actual card photo, not stock. */}
        <div className="pointer-events-none absolute bottom-2 left-2 z-10 inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/55 px-2 py-0.5 text-[9px] font-medium text-white/85 backdrop-blur-sm">
          <Camera size={9} />
          Actual card photo
        </div>

        {/* Enlarge affordance — top-right corner. Reveals on hover for
            mouse users, always visible on touch devices so mobile buyers
            see the affordance without needing to hover. */}
        <div className="pointer-events-none absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/45 px-2 py-0.5 text-[9px] text-white/70 opacity-0 backdrop-blur-sm transition-opacity group-hover/enlarge:opacity-100 [@media(hover:none)]:opacity-100">
          <ZoomIn size={9} />
          Tap to enlarge
        </div>
      </button>

      {backUrl && (
        <button
          type="button"
          onClick={() => setFlipped(!flipped)}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground"
        >
          <RefreshCw size={11} />
          {flipped ? "Show Front" : "Show Back"}
        </button>
      )}
    </div>
  );
}

function ConditionBadge({ condition }: { condition: ConditionCode }) {
  const cfg = conditionConfig[condition];
  return (
    <div className="flex items-start gap-2.5">
      <span
        className="shop-mono flex-shrink-0 rounded-sm border px-2.5 py-1 text-[12px] font-bold tracking-wider"
        style={{
          background: `${cfg.color}18`,
          borderColor: `${cfg.color}40`,
          color: cfg.color,
        }}
      >
        {condition}
      </span>
      <div>
        <p className="text-[13px] font-semibold" style={{ color: cfg.color }}>
          {cfg.label}
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{cfg.desc}</p>
      </div>
    </div>
  );
}

function GradedBadge({ company }: { company: string }) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-sm border px-3 py-1.5"
      style={{
        background:
          "linear-gradient(135deg, rgba(201,168,76,0.15), rgba(240,201,110,0.08))",
        borderColor: "rgba(201,168,76,0.4)",
        boxShadow: "0 0 20px rgba(201,168,76,0.15)",
      }}
    >
      <div
        className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm"
        style={{ background: "linear-gradient(135deg, #c9a84c, #f0c96e)" }}
      >
        <Check size={9} style={{ color: "#080b14" }} />
      </div>
      <span
        className="text-[12px] font-bold tracking-wide"
        style={{ color: "#f0c96e" }}
      >
        Professionally Graded
      </span>
      <span
        className="shop-mono rounded-sm px-1.5 py-0.5 text-[11px] font-bold"
        style={{
          background: "rgba(240,201,110,0.15)",
          color: "#c9a84c",
        }}
      >
        {company}
      </span>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/[0.05] py-2.5 last:border-b-0">
      <span className="flex-shrink-0 text-[12px] text-muted-foreground">
        {label}
      </span>
      <span className="text-right text-[12px] text-foreground/80">{value}</span>
    </div>
  );
}

function PokemonTypeChip({ type }: { type: string }) {
  const cfg = pokeTypeColor[type] ?? {
    bg: "rgba(122,138,170,0.15)",
    text: "#7a8aaa",
    icon: null,
  };
  return (
    <span
      className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-semibold"
      style={{ background: cfg.bg, color: cfg.text }}
    >
      {cfg.icon}
      {type}
    </span>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

function ShopCardDetail() {
  const { id } = Route.useParams();
  const cart = useShopCart();
  // TNK-124 phase G: lookup hook handles the warm-cache + single-card
  // fetch fallback, so deep links land cleanly even when the shop
  // layout's first-page warm doesn't include this id.
  const lookupIds = useMemo(() => [id], [id]);
  const lookup = useStorefrontCardLookup(lookupIds);

  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const card = lookup.cards[0] ?? null;

  // TNK-117 phase D: when the card isn't in the active inventory,
  // probe the sold archive so deep links to shipped cards land on a
  // real page instead of a "Card not found" dead end. Now fires only
  // once the active lookup has settled to a `not-found` state.
  const [soldCard, setSoldCard] = useState<SoldCard | null>(null);
  const [soldProbe, setSoldProbe] = useState<"idle" | "loading" | "done">(
    "idle",
  );
  useEffect(() => {
    if (lookup.phase !== "ready") return;
    if (card) return;
    setSoldProbe("loading");
    let cancelled = false;
    void (async () => {
      const out = await fetchStorefrontSoldCard(id).catch(() => null);
      if (cancelled) return;
      setSoldCard(out);
      setSoldProbe("done");
    })();
    return () => {
      cancelled = true;
    };
  }, [lookup.phase, card, id]);

  useEffect(() => {
    setRemoveConfirm(false);
    setLightboxOpen(false);
  }, [card?.id]);

  if (lookup.phase === "loading") {
    return <p className="py-12 text-center text-muted-foreground">Loading…</p>;
  }
  if (lookup.phase === "error") {
    return (
      <p className="py-12 text-center text-destructive">
        Couldn’t load this card.
      </p>
    );
  }
  if (!card) {
    // Still probing the sold archive — show a light loader so we don't
    // flash "Card not found" then swap to the sold layout.
    if (soldProbe === "idle" || soldProbe === "loading") {
      return (
        <p className="py-12 text-center text-muted-foreground">Loading…</p>
      );
    }
    // TNK-117 phase D: render a sold state when the card is in the
    // sold archive — search-engine continuity + buyer trust signal.
    if (soldCard) {
      return <SoldCardDetail card={soldCard} />;
    }
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex min-h-[60vh] flex-col items-center justify-center gap-6 px-6"
      >
        <AlertTriangle size={40} className="text-muted-foreground" />
        <div className="text-center">
          <h2 className="shop-display text-xl font-bold text-foreground">
            Card not found
          </h2>
          <p className="mt-2 text-[13px] text-muted-foreground">
            This card may have been sold or removed from the catalogue.
          </p>
        </div>
        <Link
          to="/shop/inventory"
          className="inline-flex items-center gap-2 rounded border border-primary/30 bg-primary/10 px-5 py-2.5 text-[13px] font-semibold text-primary hover:bg-primary/20"
        >
          <ArrowLeft size={14} />
          Back to inventory
        </Link>
      </motion.div>
    );
  }

  const rarity = asRarity(card.rarity);
  const rarityCfg = rarityConfig[rarity];
  const game = asGame(card.game);
  const gameCfg = gameAccent[game];
  const condition = asCondition(card.condition);
  const isReserved = cart.has(card.id);
  // TNK-119: route to the eBay auction page when the card has an
  // active eBay auction listing. Fixed-price listings keep the local
  // Reserve flow alongside the eBay link (operator decision per the
  // ticket; matches buyer-shipping perks that don't carry to eBay).
  const isAuction = card.listingType === "Auction" && !!card.ebayListingUrl;
  const price = storefrontPrice(card);

  // Build conditional meta rows — only render fields the card actually
  // carries. TNK-115 phase B1 expanded the storefront API; phase A only
  // had Set / Number / Variant / Finish.
  const metaRows: { label: string; value: React.ReactNode }[] = [];
  metaRows.push({ label: "Set", value: card.setName });
  metaRows.push({
    label: "Number",
    value: <span className="shop-mono text-[#4d9ef7]">{card.cardNumber}</span>,
  });
  if (card.variant) metaRows.push({ label: "Variant", value: card.variant });
  if (card.finish) {
    metaRows.push({
      label: "Finish",
      value: (
        <span
          className="rounded-sm border px-1.5 py-0.5 text-[11px]"
          style={{
            background: "rgba(168,85,247,0.15)",
            borderColor: "rgba(168,85,247,0.3)",
            color: "#d8b4fe",
          }}
        >
          {card.finish}
        </span>
      ),
    });
  }
  if (card.year) metaRows.push({ label: "Year", value: card.year });
  if (card.language)
    metaRows.push({
      label: "Language",
      value: <span className="text-[#fb923c]">{card.language}</span>,
    });
  if (card.tcgSeries) metaRows.push({ label: "Series", value: card.tcgSeries });
  if (card.tcgArtist)
    metaRows.push({ label: "Illustrator", value: card.tcgArtist });
  if (card.tcgPokedexNumbers && card.tcgPokedexNumbers.length > 0) {
    metaRows.push({
      label: card.tcgPokedexNumbers.length === 1 ? "Pokédex No." : "Pokédex",
      value: card.tcgPokedexNumbers
        .map((n) => `#${String(n).padStart(3, "0")}`)
        .join(", "),
    });
  }
  // TNK-121: Magic colors row. Empty array = colorless artifact / land
  // (different from null, which means "not a Magic card") — render the
  // explicit "Colorless" string in that case so the buyer doesn't
  // mistake an artifact for a multicolor card with hidden pips.
  if (card.mtgColors !== null && card.game === "Magic") {
    metaRows.push({
      label: card.mtgColors.length === 1 ? "Color" : "Colors",
      value:
        card.mtgColors.length === 0 ? (
          <span className="text-muted-foreground">Colorless</span>
        ) : (
          <ManaCost
            cost={card.mtgColors.map((c) => `{${c}}`).join("")}
            size={14}
          />
        ),
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      {/* Top rarity-tinted glow strip */}
      <div
        className="pointer-events-none fixed inset-x-0 top-0 z-10 h-px"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${rarityCfg.color} 30%, ${rarityCfg.color} 70%, transparent 100%)`,
          opacity: 0.5,
        }}
      />

      {/* Back nav */}
      <div className="mx-auto max-w-3xl px-4 pb-2 pt-5 sm:px-6">
        <Link
          to="/shop/inventory"
          className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={14} />
          Back to inventory
        </Link>
      </div>

      <div className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:gap-10">
          {/* LEFT: Image */}
          <div className="w-full md:sticky md:top-6 md:w-64 md:flex-shrink-0">
            <CardImage
              frontRef={card.imageFrontRef}
              backRef={card.imageBackRef}
              name={card.name}
              rarity={rarity}
              onEnlarge={() => setLightboxOpen(true)}
            />

            {/* Graded badge — below image when present (desktop + mobile) */}
            {card.graded && card.gradingCompany && (
              <div className="mt-3 flex justify-center">
                <GradedBadge company={card.gradingCompany} />
              </div>
            )}

            {/* Condition — desktop only, below image */}
            <div className="mt-4 hidden rounded-lg border border-white/[0.06] bg-[#0d1220] p-3 md:block">
              <ConditionBadge condition={condition} />
            </div>
          </div>

          {/* RIGHT: Info */}
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            {/* Game + rarity + finish pills */}
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="rounded-sm border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider"
                style={{
                  background: gameCfg.bg,
                  borderColor: `${gameCfg.primary}30`,
                  color: gameCfg.primary,
                }}
              >
                {game}
              </span>
              <span
                className="rounded-sm border px-2.5 py-1 text-[11px] font-semibold"
                style={{
                  background: `${rarityCfg.color}14`,
                  borderColor: `${rarityCfg.color}38`,
                  color: rarityCfg.color,
                }}
              >
                {rarity}
              </span>
              {card.finish && (
                <span
                  className="rounded-sm border px-2.5 py-1 text-[11px] font-semibold"
                  style={{
                    background: "rgba(168,85,247,0.12)",
                    borderColor: "rgba(168,85,247,0.3)",
                    color: "#d8b4fe",
                  }}
                >
                  ✦ {card.finish}
                </span>
              )}
            </div>

            {/* Card name */}
            <div>
              <h1 className="shop-display text-[clamp(20px,5vw,28px)] font-extrabold leading-tight tracking-wide text-foreground">
                {card.name}
              </h1>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {card.setName}
                <span className="mx-1.5 text-[#1e2d4a]">·</span>
                <span className="shop-mono text-[11px] text-[#4d9ef7]">
                  {card.cardNumber}
                </span>
              </p>
            </div>

            {/* Type / subtype chips — what KIND of card this is.
                Filter out legacy junk values that leaked from older
                eBay parsers (e.g. "Magic: The Gathering" from the
                *C:Game column landing in tcgTypes pre-TNK-128). */}
            {(() => {
              const cleanTypes = (card.tcgTypes ?? []).filter(
                (t) => t && t !== "Magic: The Gathering",
              );
              const cleanSubtypes = card.tcgSubtypes ?? [];
              if (cleanTypes.length === 0 && cleanSubtypes.length === 0)
                return null;
              return (
                <div className="flex flex-wrap items-center gap-2">
                  {cleanTypes.map((t) => (
                    <PokemonTypeChip key={t} type={t} />
                  ))}
                  {cleanSubtypes.map((s) => (
                    <span
                      key={s}
                      className="rounded border border-white/[0.07] bg-white/[0.04] px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              );
            })()}

            {/* Card information — mana cost / HP / P&T / Loyalty in a single
                stat row below the attribute chips. Each stat hides itself
                when null so the row collapses cleanly for cards that don't
                carry it. */}
            {(card.mtgManaCost ||
              card.tcgHp ||
              card.mtgPowerToughness ||
              card.mtgLoyalty) && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {card.mtgManaCost && (
                  <div className="flex items-baseline gap-1.5">
                    <ManaCost cost={card.mtgManaCost} size={18} />
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      Cost
                    </span>
                  </div>
                )}
                {card.tcgHp && (
                  <div className="shop-mono flex items-baseline gap-1">
                    <span className="text-[22px] font-extrabold leading-none text-foreground">
                      {card.tcgHp}
                    </span>
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      HP
                    </span>
                  </div>
                )}
                {card.mtgPowerToughness && (
                  <div className="shop-mono flex items-baseline gap-1">
                    <span className="text-[22px] font-extrabold leading-none text-foreground">
                      {card.mtgPowerToughness}
                    </span>
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      P/T
                    </span>
                  </div>
                )}
                {card.mtgLoyalty && !card.mtgPowerToughness && (
                  <div className="shop-mono flex items-baseline gap-1">
                    <span
                      className="text-[22px] font-extrabold leading-none"
                      style={{ color: "#f0c96e" }}
                    >
                      ◆ {card.mtgLoyalty}
                    </span>
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      Loyalty
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Graded badge — mobile only (desktop shows below image) */}
            {card.graded && card.gradingCompany && (
              <div className="md:hidden">
                <GradedBadge company={card.gradingCompany} />
              </div>
            )}

            {/* Condition — mobile only */}
            <div className="rounded-lg border border-white/[0.06] bg-[#0d1220] p-3 md:hidden">
              <ConditionBadge condition={condition} />
            </div>

            {/* Price + CTA card */}
            <div
              className="flex flex-col gap-4 rounded-xl border p-4"
              style={{
                background: "linear-gradient(135deg, #0d1627 0%, #0b1120 100%)",
                borderColor: "rgba(201,168,76,0.14)",
                boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 40px ${rarityCfg.glow}`,
              }}
            >
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="mb-1 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    Shop Price
                  </p>
                  <p className="shop-mono text-[clamp(28px,6vw,36px)] font-bold leading-none tracking-tight text-primary">
                    ${price.toFixed(2)}
                    <span className="ml-1 text-[12px] font-normal text-muted-foreground">
                      USD
                    </span>
                  </p>
                </div>

                {/* TNK-119: when the card has an active eBay auction
                    listing, the buyer must go bid on eBay — local
                    reservation can't honor a card whose price is being
                    determined by an open auction. Fixed-price (BIN)
                    listings keep the Reserve flow alongside the eBay
                    link (operator can still match offers locally). */}
                {isAuction ? (
                  <motion.a
                    key="bid"
                    href={card.ebayListingUrl ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="shop-display inline-flex items-center gap-2 whitespace-nowrap rounded-lg border px-5 py-3 text-[13px] font-bold tracking-wide transition-shadow"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(168,85,247,0.28), rgba(168,85,247,0.16))",
                      borderColor: "rgba(168,85,247,0.5)",
                      color: "#c084fc",
                      boxShadow: "0 4px 20px rgba(168,85,247,0.2)",
                    }}
                  >
                    <Gavel size={15} />
                    Bid on eBay
                    <ExternalLink size={13} />
                  </motion.a>
                ) : (
                  <AnimatePresence mode="wait">
                    {!isReserved ? (
                      <motion.button
                        key="add"
                        type="button"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={() => cart.add(card.id)}
                        className="shop-display inline-flex items-center gap-2 whitespace-nowrap rounded-lg border px-5 py-3 text-[13px] font-bold tracking-wide transition-shadow"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(201,168,76,0.28), rgba(240,201,110,0.16))",
                          borderColor: "rgba(201,168,76,0.5)",
                          color: "#f0c96e",
                          boxShadow: "0 4px 20px rgba(201,168,76,0.2)",
                        }}
                      >
                        <ShoppingBag size={15} />
                        Reserve This Card
                      </motion.button>
                    ) : (
                      <motion.div
                        key="reserved"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-[13px] font-semibold"
                        style={{
                          background: "rgba(74,222,128,0.12)",
                          borderColor: "rgba(74,222,128,0.3)",
                          color: "#4ade80",
                        }}
                      >
                        <Check size={14} />
                        In your reservation
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>

              {/* Reservation / auction micro-copy */}
              <div className="flex items-start gap-2 border-t border-white/[0.05] pt-3">
                <div
                  className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border"
                  style={{
                    background: isAuction
                      ? "rgba(168,85,247,0.15)"
                      : "rgba(77,158,247,0.15)",
                    borderColor: isAuction
                      ? "rgba(168,85,247,0.3)"
                      : "rgba(77,158,247,0.3)",
                  }}
                >
                  <span
                    className="text-[9px] font-extrabold"
                    style={{ color: isAuction ? "#c084fc" : "#4d9ef7" }}
                  >
                    i
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {isAuction
                    ? "Card is currently up for auction on eBay — bid there to win it."
                    : "Reservations are requests — the seller confirms availability and follows up by email."}
                </p>
              </div>

              {/* Remove link (suppressed for auction — buyer can't have
                   reserved an auction card locally). */}
              <AnimatePresence>
                {isReserved && !isAuction && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    {!removeConfirm ? (
                      <button
                        type="button"
                        onClick={() => setRemoveConfirm(true)}
                        className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground transition-colors hover:text-destructive"
                      >
                        <X size={11} />
                        Remove from reservation
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-muted-foreground">
                          Remove this card?
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            cart.remove(card.id);
                            setRemoveConfirm(false);
                          }}
                          className="text-[11px] font-semibold text-destructive"
                        >
                          Yes, remove
                        </button>
                        <button
                          type="button"
                          onClick={() => setRemoveConfirm(false)}
                          className="text-[11px] text-muted-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Rules text intentionally hidden — see TNK-121 follow-up.
                The buyer reads rules text on the card itself; the storefront
                surface stays scannable. mtgOracleText still rides on the
                storefront payload so a future toggle / detail tab can
                re-enable it without another schema cycle. */}

            {/* Card details table */}
            <div className="overflow-hidden rounded-lg border border-white/[0.07]">
              <div className="border-b border-white/[0.06] bg-white/[0.03] px-4 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Card Details
                </p>
              </div>
              <div className="bg-[#0d1220] px-4">
                {metaRows.map(({ label, value }) => (
                  <MetaRow key={label} label={label} value={value} />
                ))}
              </div>
            </div>

            {/* Card id (debug-y but useful for the buyer to quote on inquiry) */}
            <p className="shop-mono text-[10px] text-[#2a3550]">{card.id}</p>
          </div>
        </div>
      </div>

      {/* Lightbox — fullscreen photo inspection from the trust-badge tap. */}
      <CardLightbox
        card={
          lightboxOpen
            ? {
                name: card.name,
                imageFrontRef: card.imageFrontRef,
                imageBackRef: card.imageBackRef,
              }
            : null
        }
        onClose={() => setLightboxOpen(false)}
      />
    </motion.div>
  );
}

/**
 * Sold-state card detail (TNK-117 phase D). Stripped-down layout
 * matched to the active page's vocabulary so deep links to shipped
 * cards stay informative — buyer sees the card, knows it sold, gets
 * a path back to live inventory. No price, no reservation CTA.
 */
function SoldCardDetail({ card }: { card: SoldCard }) {
  const rarity = asRarity(card.rarity);
  const rarityCfg = rarityConfig[rarity];
  const game = asGame(card.game);
  const gameCfg = gameAccent[game];
  const frontUrl = card.imageFrontRef
    ? imageUrl(card.imageFrontRef, "medium")
    : null;
  const soldDate = card.soldAt
    ? new Date(card.soldAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mx-auto max-w-5xl px-4 py-6 sm:px-6"
    >
      <Link
        to="/shop/inventory"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft size={12} />
        Back to inventory
      </Link>

      <div className="mt-4 grid gap-6 sm:grid-cols-[280px_1fr]">
        <div
          className="relative w-full max-w-[280px] overflow-hidden rounded-xl border"
          style={{
            aspectRatio: "2.5/3.5",
            background: "linear-gradient(160deg, #0d1627 0%, #0a0f1e 100%)",
            borderColor: `${rarityCfg.color}22`,
            boxShadow: `0 20px 60px rgba(0,0,0,0.7)`,
          }}
        >
          {frontUrl && (
            <img
              src={frontUrl}
              alt={card.name}
              className="h-full w-full object-contain"
              style={{ opacity: 0.7, filter: "grayscale(0.3)" }}
            />
          )}
          <span
            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
            style={{
              background: "rgba(74,222,128,0.18)",
              borderColor: "rgba(74,222,128,0.45)",
              color: "#4ade80",
            }}
          >
            <Check size={10} />
            Sold
          </span>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{
                background: `${gameCfg.bg}`,
                borderColor: `${gameCfg.primary}66`,
                color: gameCfg.primary,
              }}
            >
              {card.game}
            </span>
            <span
              className="rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{
                background: `${rarityCfg.color}22`,
                borderColor: `${rarityCfg.color}55`,
                color: rarityCfg.color,
              }}
            >
              {card.rarity}
            </span>
          </div>
          <h1 className="shop-display text-2xl font-bold text-foreground">
            {card.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {card.setName}
            {card.setCode ? ` · ${card.setCode}` : ""}
            {card.cardNumber ? ` · #${card.cardNumber}` : ""}
          </p>
          {soldDate && (
            <div
              className="mt-2 inline-flex items-center gap-2 self-start rounded-md border px-3 py-2 text-sm font-semibold"
              style={{
                background: "rgba(74,222,128,0.12)",
                borderColor: "rgba(74,222,128,0.35)",
                color: "#4ade80",
              }}
            >
              <Check size={14} />
              Sold {soldDate}
            </div>
          )}
          <Link
            to="/shop/inventory"
            className="mt-3 inline-flex items-center gap-2 self-start rounded-md border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/20"
          >
            Browse similar cards
            <ArrowLeft size={14} className="rotate-180" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
